import { useState } from "react";
import { Gavel } from "lucide-react";
import { toast } from "sonner";
import { AdminCreditDecision } from "@/lib/api";

const DECISIONS = ["APPROVE", "MODIFY", "DEFER", "REJECT"];
const inr = (v) => (v == null ? "—" : `₹${Number(v).toLocaleString("en-IN", { maximumFractionDigits: 0 })}`);
const inputCls = "w-full rounded-lg bg-[#0A1F44] border border-white/15 focus:border-[#D4AF37] outline-none px-3 min-h-[38px] text-white text-sm";

/** Mandatory human credit decision (Approve / Modify / Defer / Reject) with amount, comments, basis, reviewer, timestamp. */
export const CreditDecisionForm = ({ checkId, current, canDecide, onSaved }) => {
  const [form, setForm] = useState({ decision: current?.decision || "", approved_amount_inr: current?.approved_amount_inr ?? "", comments: "", basis: "" });
  const [busy, setBusy] = useState(false);
  const needsAmount = ["APPROVE", "MODIFY"].includes(form.decision);
  const valid = form.decision && form.comments.trim().length >= 3 && form.basis.trim().length >= 3 && (!needsAmount || Number(form.approved_amount_inr) > 0);
  const save = async () => {
    setBusy(true);
    try {
      const d = await AdminCreditDecision.submit(checkId, { ...form, approved_amount_inr: form.approved_amount_inr === "" ? null : Number(form.approved_amount_inr) });
      toast.success(`Credit decision recorded: ${d.credit_decision.decision}`); onSaved?.(d.credit_decision);
    } catch (e) { toast.error(e?.response?.data?.detail?.message_en || e?.response?.data?.detail || "Could not save decision"); }
    finally { setBusy(false); }
  };
  return (
    <div data-testid="credit-decision-form" className="mt-3 rounded-xl border border-[#D4AF37]/25 bg-black/20 p-3">
      <div className="text-[10px] uppercase tracking-[0.22em] text-[#D4AF37] inline-flex items-center gap-1.5"><Gavel size={12} /> Credit Decision · Human Review (mandatory before any conditional offer)</div>
      {current && (
        <div data-testid="credit-decision-current" className="mt-2 text-[11px] text-white/80">
          Current: <strong className="text-[#F0D788]">{current.decision}</strong> · Approved amount {inr(current.approved_amount_inr)} · Reviewed by {current.reviewed_by} · {new Date(current.reviewed_at).toLocaleString()} · Policy {current.policy_ref?.version ? `v${current.policy_ref.version}` : current.policy_ref?.source}
          <div className="text-white/60 mt-0.5">Basis: {current.basis}</div>
        </div>
      )}
      {canDecide && (
        <div className="mt-3 grid gap-2 sm:grid-cols-2">
          <label className="text-[10px] uppercase tracking-wider text-white/60">Credit decision
            <select data-testid="credit-decision-select" value={form.decision} onChange={(e) => setForm({ ...form, decision: e.target.value })} className={`mt-1 ${inputCls}`}>
              <option value="">Select…</option>{DECISIONS.map((d) => <option key={d} value={d}>{d}</option>)}
            </select>
          </label>
          <label className="text-[10px] uppercase tracking-wider text-white/60">Reviewer approved amount (₹){needsAmount ? " *" : ""}
            <input data-testid="credit-decision-amount" inputMode="numeric" value={form.approved_amount_inr} onChange={(e) => setForm({ ...form, approved_amount_inr: e.target.value.replace(/[^\d]/g, "") })} className={`mt-1 ${inputCls}`} />
          </label>
          <label className="text-[10px] uppercase tracking-wider text-white/60">Reviewer comments *
            <textarea data-testid="credit-decision-comments" rows={2} value={form.comments} onChange={(e) => setForm({ ...form, comments: e.target.value })} className={`mt-1 ${inputCls} py-2`} />
          </label>
          <label className="text-[10px] uppercase tracking-wider text-white/60">Reason / basis *
            <textarea data-testid="credit-decision-basis" rows={2} value={form.basis} onChange={(e) => setForm({ ...form, basis: e.target.value })} className={`mt-1 ${inputCls} py-2`} />
          </label>
          <div className="sm:col-span-2 flex items-center justify-between gap-2 text-[10px] text-white/50">
            <span>Reviewed by / date-time are recorded automatically from your login.</span>
            <button data-testid="credit-decision-save" onClick={save} disabled={!valid || busy} className="btn-gold min-h-[38px] px-4 rounded-full text-xs font-semibold disabled:opacity-40">{busy ? "Saving…" : "Record decision"}</button>
          </div>
        </div>
      )}
    </div>
  );
};
